import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { isMobile } from "react-device-detect";
import type { MachineDto } from "../Models/LogSearchResultDto";
import { SlackViewMobileMachineCard } from "./SlackViewMobileMachineCard";
import SearchIcon from "@mui/icons-material/Search";
import { SlackViewUIContext } from "../Context/SlackViewUIContext";
import { GlobalContext } from "../../../context/GlobalContext";
import { useMachinesContext } from "../../../context/MachinesContext";
import { MachinesGroupType } from "../Models/MachinesGroupType";

interface ReadMachine {
  machineId: string;
  postCount: number;
}

interface SlackViewMobileMachinesGroupProps {
  groupTitle: string;
  machineDto: { [key: string]: MachineDto[] };
  readMachines: ReadMachine[];
  setReadMachine: (id: string, value: boolean, postCount: number) => void;
  groupType: MachinesGroupType;
}

export const SlackViewMobileMachinesGroup: React.FC<
  SlackViewMobileMachinesGroupProps
> = ({ groupTitle, machineDto, readMachines, setReadMachine, groupType }) => {
  const [DEFAULT_PAGE_SIZE, setDEFAULT_PAGE_SIZE] = useState<number>(15);
  const [visibleMachines, setVisibleMachines] =
    useState<number>(DEFAULT_PAGE_SIZE);

  const { isSearchOpen, setIsSearchOpen } = useContext(SlackViewUIContext);
  const {
    setMachineId,
    setLog,
    setShow,
    setGlobalPoints,
    setGlobalStatus,
    setMachine,
    setBacktoMenu,
  } = useContext(GlobalContext);

  const machinesContext = useMachinesContext();
  const groupContainerRef = useRef<HTMLDivElement>(null);

  const machines = useMemo(() => {
    if (!machineDto) return [];
    return Object.values(machineDto).flat();
  }, [machineDto]);

  const isInfiniteScrollPaginationEnabled = useMemo(
    () =>
      groupType === MachinesGroupType.PastEntries ||
      groupType === MachinesGroupType.WithoutFlag,
    [groupType]
  );

  useEffect(() => {
    if (isInfiniteScrollPaginationEnabled && groupContainerRef.current) {
      const calculatePageSize = () => {
        const viewportHeight = window.innerHeight;
        const containerRect =
          groupContainerRef.current?.getBoundingClientRect();

        if (containerRect) {
          const containerTop = containerRect.top;
          const availableHeight = viewportHeight - containerTop;
          const cardHeight = 42;
          const calculatedPageSize = Math.floor(availableHeight / cardHeight);
          const newPageSize = Math.max(calculatedPageSize, 5) + 5;
          setDEFAULT_PAGE_SIZE(newPageSize);
        }
      };

      calculatePageSize();
      window.addEventListener("resize", calculatePageSize);

      return () => {
        window.removeEventListener("resize", calculatePageSize);
      };
    }
  }, [isInfiniteScrollPaginationEnabled]);

  useEffect(() => {
    if (
      isInfiniteScrollPaginationEnabled &&
      groupType === MachinesGroupType.WithoutFlag
    ) {
      setVisibleMachines(DEFAULT_PAGE_SIZE);
    }
  }, [DEFAULT_PAGE_SIZE, isInfiniteScrollPaginationEnabled, groupType]);

  const visibleMachinesList = useMemo(() => {
    return machines.slice(0, visibleMachines);
  }, [machines, visibleMachines]);

  const onSearchButtonClick = useCallback(() => {
    setIsSearchOpen(!isSearchOpen);
  }, [isSearchOpen, setIsSearchOpen]);

  const openLog = useCallback(
    (machineId: string) => {
      const machine = machines.find(
        (machine) => machine.MachineId === machineId
      );

      setReadMachine(machineId, true, machine?.PostCount ?? 0);
      setMachineId(machineId);
      setLog(true);
      setBacktoMenu(true);
    },
    [machines, setBacktoMenu, setLog, setMachineId, setReadMachine]
  );

  const openContextMenu = useCallback(
    (
      e:
        | React.MouseEvent<HTMLDivElement, MouseEvent>
        | React.TouchEvent<HTMLDivElement>,
      machineId: string,
      position?: { x: number; y: number }
    ) => {
      let x = 0;
      let y = 0;

      if (position) {
        x = position.x;
        y = position.y;
      } else if (isMobile && "touches" in e) {
        x = e.touches[0].clientX;
        y = e.touches[0].clientY;
      } else if ("pageX" in e) {
        x = e.pageX;
        y = e.pageY;
      }

      const allMachines = Object.values(machinesContext.propData).flat();
      const machine = allMachines.find((machine) => machine.Id === machineId);

      setShow(true);
      setGlobalPoints({ x, y });
      setGlobalStatus({
        color: machine?.EventState ?? "G",
        isUrgent: machine?.IsUrgent ?? false,
      });
      setMachine(machine);
      setMachineId(machine?.Id ?? "");

      if (isMobile) {
        document.documentElement.style.overflow = "hidden";
      }
    },
    [
      machinesContext.propData,
      setShow,
      setGlobalPoints,
      setGlobalStatus,
      setMachine,
      setMachineId,
    ]
  );

  const closeContextMenu = useCallback(() => {
    setShow(false);
    setGlobalPoints({ x: 0, y: 0 });
    setGlobalStatus({});
    setMachineId("");
    setMachine(null);
  }, [setGlobalPoints, setGlobalStatus, setMachine, setMachineId, setShow]);

  const checkIfMachineIsRead = useCallback(
    (machine: MachineDto) =>
      groupType === MachinesGroupType.YesterdayLogs &&
      !!readMachines.find(
        (rm) =>
          rm.machineId === machine.MachineId &&
          rm.postCount >= machine.PostCount
      ),
    [groupType, readMachines]
  );

  const RenderMachines = () => {
    if (isInfiniteScrollPaginationEnabled) {
      return (
        <InfiniteScroll
          dataLength={visibleMachinesList.length}
          scrollableTarget={"machines-scrollable"}
          next={() => {
            setVisibleMachines((prev) =>
              Math.min(prev + DEFAULT_PAGE_SIZE, machines.length)
            );
          }}
          hasMore={visibleMachines < machines.length}
          loader={<></>}
          style={{
            height: "auto",
            overflow: "visible",
          }}
        >
          {visibleMachinesList.map((m) => (
            <div
              style={{ width: "100%" }}
              key={m.MachineId}
              className="slack-mobile-machine-item"
            >
              <SlackViewMobileMachineCard
                handleMenuClose={closeContextMenu}
                onMachineRightClick={openContextMenu}
                onMachineLeftClick={openLog}
                groupType={groupType}
                machine={m}
                isRead={checkIfMachineIsRead(m)}
                setReadMachine={setReadMachine}
              />
            </div>
          ))}
        </InfiniteScroll>
      );
    } else {
      return machines.map((m) => (
        <div
          style={{ width: "100%" }}
          key={m.MachineId}
          className="slack-mobile-machine-item"
        >
          <SlackViewMobileMachineCard
            handleMenuClose={closeContextMenu}
            onMachineRightClick={openContextMenu}
            onMachineLeftClick={openLog}
            groupType={groupType}
            machine={m}
            isRead={checkIfMachineIsRead(m)}
            setReadMachine={setReadMachine}
          />
        </div>
      ));
    }
  };

  return (
    <div
      ref={groupContainerRef}
      style={{
        marginTop:
          groupType === MachinesGroupType.YesterdayLogs ? "15px" : undefined,
        marginBottom: isInfiniteScrollPaginationEnabled ? "0" : "15px",
        width: "100%",
        paddingBottom: isInfiniteScrollPaginationEnabled ? "0" : "15px",
        borderBottom: "1px solid rgba(255, 255, 255, 0.50)",
        paddingLeft: "15px",
      }}
      className="slack-mobile-group-container"
    >
      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          width: "100%",
        }}
      >
        <h3
          style={{
            color: "#FFF",
            fontFamily: "Lato",
            fontSize: "18px",
            fontStyle: "normal",
            fontWeight: "700",
          }}
          className="slack-mobile-group-title"
        >
          {groupTitle}
        </h3>
        {groupType === MachinesGroupType.YesterdayLogs && (
          <SearchIcon
            style={{
              color: "white",
              cursor: "pointer",
              borderRadius: "16px",
              backgroundColor: "#A665A8",
              height: "24px",
              width: "24px",
              padding: "1px",
              marginLeft: "10px",
            }}
            onClick={onSearchButtonClick}
          />
        )}
      </div>
      <div className="slack-mobile-group-content">{RenderMachines()}</div>
    </div>
  );
};
